/**
 * Akamai EdgeScape (`X-Akamai-Edgescape`) for server-side geo parsing.
 *
 * `sanitizeIncomingHeadersForGeo` only keeps `x-akamai-edgescape` for `dataMap._incomingHeaders`
 * so downstream code reads EdgeScape via `parseAkamaiGeo`. Omits cookies, auth, and other headers.
 */

const ALLOWED_HEADER_KEYS = new Set([
  "x-akamai-edgescape",
  "x-forwarded-for",
  "true-client-ip",
  "x-forwarded-proto",
  "cf-connecting-ip",
]);

/**
 * Copy only geo-related headers for downstream parsing. Omits cookie, authorization, etc.
 * @param {import('http').IncomingHttpHeaders} headers
 * @returns {Record<string, string | string[] | undefined>}
 */
export function sanitizeIncomingHeadersForGeo(headers = {}) {
  const out = {};
  if (!headers || typeof headers !== "object") return out;
  Object.keys(headers).forEach((k) => {
    const lower = k.toLowerCase();
    if (ALLOWED_HEADER_KEYS.has(lower)) {
      out[lower] = headers[k];
    }
  });
  return out;
}

function parseEdgeScapeString(str) {
  if (!str || typeof str !== "string") return {};
  const out = {};
  const parts = str.split(",");
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i].trim();
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const key = part.slice(0, eq).trim().toLowerCase();
    const val = part.slice(eq + 1).trim();
    if (key) out[key] = val;
  }
  return out;
}

function pickNumber(obj, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && v !== "") {
      const n = parseFloat(String(v));
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

export function parseAkamaiGeo(rawAkamaiEdgescapeHeader) {
  const rawStr = Array.isArray(rawAkamaiEdgescapeHeader)
    ? rawAkamaiEdgescapeHeader.join(",")
    : rawAkamaiEdgescapeHeader || "";
  const parsed = parseEdgeScapeString(rawStr);

  const latitude = pickNumber(parsed, ["lat", "latitude", "geo_lat"]);
  const longitude = pickNumber(parsed, ["long", "longitude", "lon", "lng", "geo_long"]);

  return {
    latitude,
    longitude,
  };
}

/**
 * Remove server-only fields before serializing `data` to the browser (SSR props).
 * @param {Record<string, unknown>} dataMap
 */
export function stripServerOnlyGeoFields(dataMap) {
  if (dataMap && Object.prototype.hasOwnProperty.call(dataMap, "_incomingHeaders")) {
    delete dataMap._incomingHeaders;
  }
  return dataMap;
}

/**
 * @param {import('next/server').NextRequest} request
 */
export function logIncomingGeoHeadersAccess(request) {
  const path = request.nextUrl?.pathname ?? "";
  const raw = Object.fromEntries(request?.headers?.entries() ?? []);

  if (process.env.VERBOSE_REQUEST_LOG === "1") {
    const payload = {
      type: "incoming_headers_verbose",
      method: request.method,
      path,
      headers: raw,
    };
    // eslint-disable-next-line no-console -- permanent access log
    console.log(JSON.stringify(payload));
    return;
  }

  const payload = {
    type: "incoming_geo_headers",
    method: request.method,
    path,
    headers: sanitizeIncomingHeadersForGeo(raw),
  };
  // eslint-disable-next-line no-console -- permanent access log
  console.log(JSON.stringify(payload));
}
